/*
 * Copyright (c) 2026-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod';

import { request } from '@/api/client';
import { pageFoundSchema, pageSpotSchema } from '@/api/counterpartyPaper';

/**
 * An Excel template (templates spec §16).
 *
 * Its questions are cells, clicked on a read-only grid the server draws from
 * the workbook; its answers are written into a copy of the workbook; and it
 * is signed on the PDF Excel makes of the sheet, with boxes placed on that
 * PDF's pages as on a counterparty's PDF. The writes that change the draft
 * live beside the draft in `api/workflows.ts`.
 */

/** The kinds of answer a cell takes. */
export const CELL_KINDS = ['text', 'area', 'date', 'number', 'money'] as const;
export type CellKind = (typeof CELL_KINDS)[number];

/** A cell that is a question, and which question fills it. */
export const sheetCellSchema = z.object({
  cell: z.string(),
  key: z.string(),
});

/** An Excel template's sheet, as the draft carries it. */
export const sheetDraftSchema = z.object({
  name: z.string(),
  area: z.string(),
  cells: z.array(sheetCellSchema),
  spots: z.array(pageSpotSchema),
  pages: z.number().nullable(),
  file: z.string(),
});

export type SheetDraft = z.infer<typeof sheetDraftSchema>;

/** One cell as the grid draws it; a merged range is its top-left cell. */
export const gridCellSchema = z.object({
  address: z.string(),
  row: z.number(),
  column: z.number(),
  text: z.string(),
  bold: z.boolean(),
  italic: z.boolean(),
  fill: z.string().nullable(),
  color: z.string().nullable(),
  align: z.enum(['center', 'right']).nullable(),
  formula: z.boolean(),
  rows: z.number(),
  columns: z.number(),
});

export type GridCell = z.infer<typeof gridCellSchema>;

/** The sheet, drawn read-only, and whether this environment can make its PDF. */
export const sheetGridSchema = z.object({
  name: z.string(),
  area: z.string(),
  first_row: z.number(),
  first_column: z.number(),
  widths: z.array(z.number()),
  heights: z.array(z.number()),
  rows: z.array(z.array(gridCellSchema)),
  can_convert: z.boolean(),
  has_print_area: z.boolean(),
});

export type SheetGrid = z.infer<typeof sheetGridSchema>;

/** The sheet as the PDF its spots are placed on: base64, its pages, its own lines. */
export const sheetPdfSchema = z.object({
  pdf: z.string(),
  pages: z.number(),
  found: pageFoundSchema,
});

export type SheetPdf = z.infer<typeof sheetPdfSchema>;

/** One question cell as the editor sends it. */
export interface SheetCellInput {
  cell: string;
  key: string;
  label: string;
  field_type: CellKind;
  is_required: boolean;
}

/**
 * The template's sheet -- the draft's, else the published one -- drawn for
 * marking its questions. Keyed by the draft's file, so a new upload is drawn
 * again and an edit to the questions is not.
 */
export const sheetGridQuery = (workflowId: string, file: string) =>
  queryOptions({
    queryKey: ['workflow', workflowId, 'sheet', file] as const,
    queryFn: ({ signal }) =>
      request(`/api/v1/workflows/${workflowId}/draft/sheet`, sheetGridSchema, { signal }),
    staleTime: Infinity,
  });

/**
 * The sheet as a PDF, its questions unanswered, made by the configured
 * converter for placing the spots on. Made for this request and kept nowhere.
 */
export const sheetPdfQuery = (workflowId: string, file: string, enabled = true) =>
  queryOptions({
    enabled,
    queryKey: ['workflow', workflowId, 'sheet-pdf', file] as const,
    queryFn: ({ signal }) =>
      request(`/api/v1/workflows/${workflowId}/draft/sheet/pdf`, sheetPdfSchema, {
        method: 'POST',
        signal,
      }),
    staleTime: Infinity,
    retry: false,
  });

/** The PDF's bytes, from the base64 the server sent them as. */
export function pdfBytes(sheet: SheetPdf): ArrayBuffer {
  const text = atob(sheet.pdf);
  const bytes = new Uint8Array(text.length);
  for (let at = 0; at < text.length; at += 1) bytes[at] = text.charCodeAt(at);
  return bytes.buffer;
}
